import { useEffect, useState, useCallback } from 'react'
import { useCamera } from '@hooks/useCamera'
import { Modal } from '@components/UI/Modal'
import { clsx } from 'clsx'
import styles from './CameraModal.module.css'

interface CapturedPhoto {
  id: string
  blob: Blob
  preview: string
}

interface CameraModalProps {
  isOpen: boolean
  onClose: () => void
  onCapture: (blobs: Blob[]) => void
  maxPhotos?: number
}

type CameraStep = 'permission' | 'camera' | 'preview' | 'gallery'

export function CameraModal({ isOpen, onClose, onCapture, maxPhotos = 10 }: CameraModalProps) {
  const {
    isActive,
    isLoading,
    hasPermission,
    permissionState,
    error,
    capturedImage,
    videoRef,
    canvasRef,
    requestPermission,
    startCamera,
    stopCamera,
    capturePhoto,
    switchCamera,
    retake
  } = useCamera({
    facingMode: 'environment',
    width: 1920,
    height: 1080
  })

  const [flashEffect, setFlashEffect] = useState(false)
  const [photos, setPhotos] = useState<CapturedPhoto[]>([])
  const [currentPhoto, setCurrentPhoto] = useState<CapturedPhoto | null>(null)
  const [step, setStep] = useState<CameraStep>('permission')
  const [permissionRequested, setPermissionRequested] = useState(false)
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(null)

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setPhotos([])
      setCurrentPhoto(null)
      setPermissionRequested(false)
      setSelectedPhotoIndex(null)
      
      // Check if we already have permission
      if (hasPermission === true || permissionState === 'granted') {
        setStep('camera')
        startCamera()
      } else if (hasPermission === false || permissionState === 'denied') {
        setStep('permission')
      } else {
        setStep('permission')
      }
    }
  }, [isOpen, hasPermission, permissionState, startCamera])

  // Cleanup when modal closes
  useEffect(() => {
    if (!isOpen) {
      stopCamera()
      // Revoke all object URLs
      photos.forEach(photo => URL.revokeObjectURL(photo.preview))
      setPhotos([])
      setCurrentPhoto(null)
      setStep('permission')
    }
  }, [isOpen, stopCamera, photos])

  const handleRequestPermission = useCallback(async () => {
    setPermissionRequested(true)
    const granted = await requestPermission()
    
    if (granted) {
      setStep('camera')
      await startCamera()
    }
  }, [requestPermission, startCamera])

  const handleCapture = useCallback(async () => {
    // Flash effect
    setFlashEffect(true)
    setTimeout(() => setFlashEffect(false), 150)
    
    // Haptic feedback (if supported)
    if (navigator.vibrate) {
      navigator.vibrate(50)
    }
    
    const blob = await capturePhoto()
    if (blob) {
      const photo: CapturedPhoto = {
        id: `photo-${Date.now()}`,
        blob,
        preview: URL.createObjectURL(blob)
      }
      setCurrentPhoto(photo)
      setStep('preview')
    }
  }, [capturePhoto])

  const handleRetake = useCallback(async () => {
    if (currentPhoto) {
      URL.revokeObjectURL(currentPhoto.preview)
    }
    setCurrentPhoto(null)
    setStep('camera')
    await retake()
  }, [retake, currentPhoto])

  const handleAddPhoto = useCallback(() => {
    if (currentPhoto) {
      setPhotos(prev => [...prev, currentPhoto])
      setCurrentPhoto(null)
      setStep('camera')
      retake()
    }
  }, [currentPhoto, retake])

  const handleUsePhotos = useCallback(() => {
    // Add current photo if exists
    const allPhotos = currentPhoto 
      ? [...photos, currentPhoto]
      : photos
    
    if (allPhotos.length > 0) {
      onCapture(allPhotos.map(p => p.blob))
      onClose()
    }
  }, [currentPhoto, photos, onCapture, onClose])

  const handleClose = useCallback(() => {
    stopCamera()
    // Revoke all object URLs
    photos.forEach(photo => URL.revokeObjectURL(photo.preview))
    if (currentPhoto) {
      URL.revokeObjectURL(currentPhoto.preview)
    }
    setPhotos([])
    setCurrentPhoto(null)
    setStep('permission')
    onClose()
  }, [stopCamera, photos, currentPhoto, onClose])

  const handleRetryCamera = useCallback(async () => {
    setStep('camera')
    await startCamera()
  }, [startCamera])

  const handleRemovePhoto = useCallback((index: number) => {
    setPhotos(prev => {
      const photo = prev[index]
      if (photo) {
        URL.revokeObjectURL(photo.preview)
      }
      return prev.filter((_, i) => i !== index)
    })
    setSelectedPhotoIndex(null)
  }, [])

  const handleViewGallery = useCallback(() => {
    if (currentPhoto) {
      setPhotos(prev => [...prev, currentPhoto])
      setCurrentPhoto(null)
    }
    setStep('gallery')
  }, [currentPhoto])

  const handleBackToCamera = useCallback(async () => {
    setStep('camera')
    await startCamera()
  }, [startCamera])

  const totalPhotos = photos.length + (currentPhoto ? 1 : 0)
  const canAddMore = totalPhotos < maxPhotos

  // Render permission request screen
  const renderPermissionScreen = () => (
    <div className={styles.permissionScreen}>
      <div className={styles.permissionContent}>
        <div className={styles.permissionIcon}>
          <i className="bi bi-camera-fill" />
        </div>
        
        <h2 className={styles.permissionTitle}>
          Accesso alla Fotocamera
        </h2>
        
        <p className={styles.permissionText}>
          Per scattare foto della bolletta, è necessario consentire l'accesso alla fotocamera del dispositivo.
        </p>
        
        <div className={styles.multiPhotoHint}>
          <i className="bi bi-images" />
          <span>Puoi scattare più foto se la bolletta ha più pagine</span>
        </div>
        
        {hasPermission === false || permissionState === 'denied' ? (
          <div className={styles.permissionDenied}>
            <div className={styles.deniedIcon}>
              <i className="bi bi-exclamation-triangle-fill" />
            </div>
            <p className={styles.deniedText}>
              L'accesso alla fotocamera è stato negato.
            </p>
            <p className={styles.deniedInstructions}>
              Per utilizzare questa funzione:
            </p>
            <ol className={styles.deniedSteps}>
              <li>Apri le <strong>Impostazioni</strong> del browser</li>
              <li>Cerca la sezione <strong>Privacy</strong> o <strong>Autorizzazioni</strong></li>
              <li>Abilita l'accesso alla <strong>Fotocamera</strong> per questo sito</li>
              <li>Ricarica la pagina e riprova</li>
            </ol>
          </div>
        ) : error ? (
          <div className={styles.permissionError}>
            <p className={styles.errorMessage}>{error}</p>
          </div>
        ) : null}
        
        <div className={styles.permissionActions}>
          {hasPermission !== false && permissionState !== 'denied' && (
            <button
              type="button"
              className={styles.allowButton}
              onClick={handleRequestPermission}
              disabled={isLoading || permissionRequested}
            >
              {isLoading ? (
                <>
                  <span className={styles.buttonSpinner} />
                  Richiesta in corso...
                </>
              ) : (
                <>
                  <i className="bi bi-camera-fill" />
                  Consenti Fotocamera
                </>
              )}
            </button>
          )}
          
          <button
            type="button"
            className={styles.cancelPermButton}
            onClick={handleClose}
          >
            Annulla
          </button>
        </div>
      </div>
    </div>
  )

  // Render camera screen
  const renderCameraScreen = () => (
    <>
      {/* Header */}
      <div className={styles.header}>
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Chiudi"
        >
          <i className="bi bi-x-lg" />
        </button>
        
        {/* Photo counter */}
        {photos.length > 0 && (
          <button
            type="button"
            className={styles.galleryButton}
            onClick={handleViewGallery}
            aria-label="Vedi galleria"
          >
            <span className={styles.photoCount}>{photos.length}</span>
            <i className="bi bi-images" />
          </button>
        )}
        
        {isActive && !capturedImage && (
          <button
            type="button"
            className={styles.switchButton}
            onClick={switchCamera}
            aria-label="Cambia fotocamera"
          >
            <i className="bi bi-arrow-repeat" />
          </button>
        )}
      </div>

      {/* Camera viewport */}
      <div className={styles.viewport}>
        {/* Loading state */}
        {isLoading && (
          <div className={styles.loadingState}>
            <div className={styles.spinner}>
              <div className={styles.spinnerRing}></div>
            </div>
            <p className={styles.loadingText}>Inizializzazione fotocamera...</p>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className={styles.errorState}>
            <div className={styles.errorIcon}>
              <i className="bi bi-exclamation-triangle" />
            </div>
            <p className={styles.errorText}>{error}</p>
            <button
              type="button"
              className={styles.retryButton}
              onClick={handleRetryCamera}
            >
              <i className="bi bi-arrow-clockwise" />
              Riprova
            </button>
          </div>
        )}

        {/* Camera feed */}
        {!error && (
          <>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              webkit-playsinline="true"
              className={clsx(styles.video, {
                [styles.hidden]: !!capturedImage || isLoading
              })}
            />
            <canvas ref={canvasRef} className={styles.canvas} />
            
            {/* Flash effect overlay */}
            <div className={clsx(styles.flash, { [styles.flashActive]: flashEffect })} />
            
            {/* Document guide frame */}
            {isActive && !capturedImage && !isLoading && (
              <div className={styles.guideFrame}>
                <div className={styles.guideCorner} data-position="tl" />
                <div className={styles.guideCorner} data-position="tr" />
                <div className={styles.guideCorner} data-position="bl" />
                <div className={styles.guideCorner} data-position="br" />
                
                <div className={styles.guideInstruction}>
                  <i className="bi bi-file-earmark" />
                  <span>
                    {photos.length === 0 
                      ? 'Posiziona la bolletta nel riquadro'
                      : `Foto ${photos.length + 1} - Inquadra la pagina successiva`
                    }
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Mini gallery preview */}
      {photos.length > 0 && (
        <div className={styles.miniGallery}>
          {photos.map((photo, index) => (
            <div key={photo.id} className={styles.miniThumb}>
              <img src={photo.preview} alt={`Foto ${index + 1}`} />
              <span className={styles.thumbNumber}>{index + 1}</span>
            </div>
          ))}
        </div>
      )}

      {/* Controls */}
      <div className={styles.controls}>
        <button
          type="button"
          className={styles.cancelButton}
          onClick={handleClose}
        >
          Annulla
        </button>
        
        <button
          type="button"
          className={clsx(styles.captureButton, {
            [styles.captureDisabled]: !isActive || isLoading
          })}
          onClick={handleCapture}
          disabled={!isActive || isLoading}
          aria-label="Scatta foto"
        >
          <div className={styles.captureRing}>
            <div className={styles.captureInner}>
              <i className="bi bi-camera-fill" />
            </div>
          </div>
        </button>
        
        {photos.length > 0 ? (
          <button
            type="button"
            className={styles.finishButton}
            onClick={handleUsePhotos}
          >
            <i className="bi bi-check-lg" />
            <span>Fine ({photos.length})</span>
          </button>
        ) : (
          <div className={styles.placeholder} />
        )}
      </div>
    </>
  )

  // Render preview screen (after each capture)
  const renderPreviewScreen = () => (
    <>
      {/* Header */}
      <div className={styles.header}>
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Chiudi"
        >
          <i className="bi bi-x-lg" />
        </button>
        
        <div className={styles.previewTitle}>
          <span>Foto {photos.length + 1}</span>
          {photos.length > 0 && (
            <span className={styles.previewSubtitle}>
              ({photos.length} foto già scattate)
            </span>
          )}
        </div>
      </div>

      {/* Preview viewport */}
      <div className={styles.viewport}>
        <div className={styles.preview}>
          <img
            src={currentPhoto?.preview || capturedImage || ''}
            alt="Foto scattata"
            className={styles.previewImage}
          />
          <div className={styles.previewBadge}>
            <i className="bi bi-check-circle-fill" />
            <span>Foto pronta</span>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className={styles.previewControls}>
        <button
          type="button"
          className={styles.retakeButton}
          onClick={handleRetake}
        >
          <i className="bi bi-arrow-clockwise" />
          <span>Rifai</span>
        </button>
        
        {canAddMore && (
          <button
            type="button"
            className={styles.addMoreButton}
            onClick={handleAddPhoto}
          >
            <i className="bi bi-plus-lg" />
            <span>Aggiungi foto</span>
          </button>
        )}
        
        <button
          type="button"
          className={styles.useButton}
          onClick={handleUsePhotos}
        >
          <i className="bi bi-check-lg" />
          <span>{photos.length > 0 ? `Usa tutte (${totalPhotos})` : 'Usa foto'}</span>
        </button>
      </div>
    </>
  )

  // Render gallery screen
  const renderGalleryScreen = () => (
    <>
      {/* Header */}
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          onClick={handleBackToCamera}
          aria-label="Torna alla fotocamera"
        >
          <i className="bi bi-arrow-left" />
        </button>
        
        <div className={styles.galleryTitle}>
          <span>Foto scattate ({photos.length})</span>
        </div>
        
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Chiudi"
        >
          <i className="bi bi-x-lg" />
        </button>
      </div>

      {/* Gallery grid */}
      <div className={styles.galleryViewport}>
        <div className={styles.galleryGrid}>
          {photos.map((photo, index) => (
            <div 
              key={photo.id} 
              className={clsx(styles.galleryItem, {
                [styles.galleryItemSelected]: selectedPhotoIndex === index
              })}
              onClick={() => setSelectedPhotoIndex(selectedPhotoIndex === index ? null : index)}
            >
              <img src={photo.preview} alt={`Foto ${index + 1}`} />
              <div className={styles.galleryItemNumber}>{index + 1}</div>
              {selectedPhotoIndex === index && (
                <button
                  type="button"
                  className={styles.removePhotoButton}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemovePhoto(index)
                  }}
                >
                  <i className="bi bi-trash" />
                </button>
              )}
            </div>
          ))}
          
          {/* Add more button */}
          {canAddMore && (
            <button
              type="button"
              className={styles.addPhotoCard}
              onClick={handleBackToCamera}
            >
              <i className="bi bi-plus-lg" />
              <span>Aggiungi</span>
            </button>
          )}
        </div>
        
        {photos.length === 0 && (
          <div className={styles.emptyGallery}>
            <i className="bi bi-images" />
            <p>Nessuna foto scattata</p>
            <button
              type="button"
              className={styles.startCaptureButton}
              onClick={handleBackToCamera}
            >
              <i className="bi bi-camera" />
              Scatta la prima foto
            </button>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className={styles.galleryControls}>
        <button
          type="button"
          className={styles.addMoreGalleryButton}
          onClick={handleBackToCamera}
        >
          <i className="bi bi-camera" />
          <span>Scatta altra foto</span>
        </button>
        
        {photos.length > 0 && (
          <button
            type="button"
            className={styles.useAllButton}
            onClick={handleUsePhotos}
          >
            <i className="bi bi-check-lg" />
            <span>Usa {photos.length} foto</span>
          </button>
        )}
      </div>
    </>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="fullscreen"
      showClose={false}
    >
      <div className={styles.container}>
        {step === 'permission' && renderPermissionScreen()}
        {step === 'camera' && renderCameraScreen()}
        {step === 'preview' && renderPreviewScreen()}
        {step === 'gallery' && renderGalleryScreen()}
      </div>
    </Modal>
  )
}
